<template>
  <form-block :title="tr('Beneficiary and payer')">
    <info-text>
      {{ tr('Starting July 10, 2023, you\'ll need to indicate the beneficiary and payer for any new or edited ad set with an audience in the European Union.') }}
    </info-text>

    <div class="flex flex-col gap-1.5">
      <ui-label>{{ tr('Beneficiary') }}</ui-label>
      <autosuggest
        v-model="entity.dsa_beneficiary"
        :input-props="{ 'disabled': disabled }"
        :suggestions="[{ data: beneficiarySuggestions }]"
        :loading="beneficiarySuggestionsIsLoading"
        @update:model-value="updateBeneficiarySuggestions"
        @selected="onSelectSuggestedBeneficiary"
      >
        <template #default="{ suggestion }">{{ suggestion.item }}</template>
      </autosuggest>
    </div>

    <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', disabled ? 'cursor-not-allowed' : 'cursor-pointer' ]">
      <ui-checkbox
        :model-value="!!differentPayer"
        :disabled="disabled"
        @update:model-value="(value) => differentPayer = value"
      />
      {{ tr('A different person or organization is paying') }}
    </label>

    <transition-expand>
      <div v-if="differentPayer" class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Payer') }}</ui-label>
        <autosuggest
          v-model="entity.dsa_payer"
          :input-props="{ 'disabled': disabled }"
          :suggestions="[{ data: payerSuggestions }]"
          :loading="payerSuggestionsIsLoading"
          @update:model-value="updatePayerSuggestions"
          @selected="onSelectSuggestedPayer"
        >
          <template #default="{ suggestion }">{{ suggestion.item }}</template>
        </autosuggest>
      </div>
    </transition-expand>
  </form-block>
</template>

<script>
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import ToolService from '@/services/Tool.js';
import Autosuggest from '@/ui/components/Autosuggest.vue';
import InfoText from '@/ui/components/InfoText.vue';
import TransitionExpand from '@/ui/components/TransitionExpand.vue';
import debounce from '@/utils/debounce.js';

export default {
  name: 'DsaBlock',
  components: {
    Autosuggest,
    InfoText,
    TransitionExpand,
  },
  mixins: [ RuleFormBlockMixin ],
  data() {
    return {
      differentPayer: this.entity ? (this.entity.dsa_payer && this.entity.dsa_payer != this.entity.dsa_beneficiary) : null,
      beneficiarySuggestions: [],
      payerSuggestions: [],
      beneficiarySuggestionsIsLoading: false,
      payerSuggestionsIsLoading: false,
      updateBeneficiarySuggestions: debounce(newValue => {
        this.beneficiarySuggestionsIsLoading = true;

        ToolService.autosuggestBeneficiaryOrPayer(newValue)
          .then(resp => this.beneficiarySuggestions = resp)
          .catch(() => this.beneficiarySuggestions = [])
          .finally(() => this.beneficiarySuggestionsIsLoading = false);
      }),
      updatePayerSuggestions: debounce(newValue => {
        this.payerSuggestionsIsLoading = true;

        ToolService.autosuggestBeneficiaryOrPayer(newValue)
          .then(resp => this.payerSuggestions = resp)
          .catch(() => this.payerSuggestions = [])
          .finally(() => this.payerSuggestionsIsLoading = false);
      }),
    };
  },
  watch: {
    differentPayer(val) {
      if (!val) {
        this.entity.dsa_payer = this.entity.dsa_beneficiary;
      }
      else {
        this.entity.dsa_payer = '';
      }
    },
    'entity.dsa_beneficiary': {
      immediate: true,
      handler() {
        if (!this.differentPayer) {
          this.entity.dsa_payer = this.entity.dsa_beneficiary;
        }
      },
    },
  },
  methods: {
    onSelectSuggestedBeneficiary(item) {
      if (item) {
        this.entity.dsa_beneficiary = item.item;
      }
    },
    onSelectSuggestedPayer(item) {
      if (item) {
        this.entity.dsa_payer = item.item;
      }
    },
    validate() {
      if (this.entity.dsa_beneficiary === null || this.entity.dsa_beneficiary.trim().length === 0) {
        this.error(this.tr(':field is required!', { ':field': this.tr('Beneficiary') }));
        return false;
      }
      if (this.entity.dsa_payer === null || this.entity.dsa_payer.trim().length === 0) {
        this.error(this.tr(':field is required!', { ':field': this.tr('Payer') }));
        return false;
      }

      return true;
    },
  },
};
</script>
